import { FC, useCallback, useState } from 'react';
import { StyledDragList } from '../AutomationFolders/styles';
import { useDragAndDropContext } from './context';
import Draggable, {
  type DraggableBounds,
  type DraggableData,
  type DraggableEvent,
} from 'react-draggable';
import Icon, { IconSizing } from '__components/Kizen/Icon';
import { Icon as KDSIcon } from '@kizen/kds/Icon';
import {
  StyledCounts,
  StyledDragContent,
  StyledDragHandle,
  StyledDragItem,
  StyledEmptyDragItem,
} from './styles';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import { AutomationOrFolder } from 'store/automationsPage/automations.redux';
import { isFolder } from './helpers';
import { isTypeGlobal } from '../helpers';
import {
  SIDEBAR_GAP,
  FOLDERS_SIDEBAR_ID,
  SIDEBAR_WIDTH,
} from '../AutomationFolders/constants';

export const DragList: FC<{
  rowData: AutomationOrFolder[];
  canEdit: boolean;
}> = ({ rowData, canEdit }) => {
  const {
    dragging,
    patching,
    onHoverIn,
    onHoverOut,
    dragItem,
    onDragStart,
    onDragEnd,
  } = useDragAndDropContext();

  const [bounds, setBounds] = useState<DraggableBounds>({
    left: -(SIDEBAR_WIDTH + SIDEBAR_GAP),
    right: 0,
    top: 0,
    bottom: 0,
  });

  const sidebar = document.getElementById(FOLDERS_SIDEBAR_ID);

  const handleDragStart = useCallback(
    (_e: DraggableEvent, data: DraggableData) => {
      const sidebarRect = sidebar!.getBoundingClientRect();
      const itemRect = data.node.getBoundingClientRect();
      setBounds((prev) => ({
        ...prev,
        top: sidebarRect.top - itemRect.top,
        bottom:
          sidebarRect.bottom +
          (window.innerHeight - sidebarRect.bottom) -
          itemRect.bottom,
      }));
      onDragStart();
    },
    [onDragStart, sidebar]
  );

  return (
    <StyledDragList>
      {
        <div onMouseLeave={onHoverOut}>
          {rowData.map((item) => {
            const handleHoverIn = () => {
              onHoverIn(item);
            };

            return dragItem?.id === item.id && !patching && canEdit ? (
              <Draggable
                key={item.id}
                onStart={handleDragStart}
                onStop={onDragEnd}
                bounds={bounds}
                offsetParent={sidebar!}
              >
                <StyledDragItem
                  dragging={dragging}
                  onMouseEnter={handleHoverIn}
                  onMouseLeave={onHoverOut}
                >
                  <StyledDragHandle dragging={dragging}>
                    <IconSizing size="15px">
                      <Icon
                        icon="reorder"
                        color="var(--color-background-dark)"
                      />
                    </IconSizing>
                  </StyledDragHandle>
                  {dragging ? (
                    <StyledDragContent>
                      {isFolder(item) ? (
                        <KDSIcon
                          icon="custom-folder"
                          color="button-secondary-default"
                          size="base"
                          className="shrink-0"
                        />
                      ) : null}
                      <TextEllipsisWithTooltip
                        color={
                          isFolder(item) ||
                          item.customObject.access?.view ||
                          isTypeGlobal(item)
                            ? 'var(--color-button-secondary-default)'
                            : undefined
                        }
                      >
                        {item.name}
                      </TextEllipsisWithTooltip>
                      {isFolder(item) ? (
                        <StyledCounts
                          as="span"
                          color="var(--color-button-secondary-default)"
                          fontStyle="italic"
                        >
                          ({item.automations_count})
                        </StyledCounts>
                      ) : null}
                    </StyledDragContent>
                  ) : null}
                </StyledDragItem>
              </Draggable>
            ) : (
              <StyledEmptyDragItem key={item.id} onMouseEnter={handleHoverIn} />
            );
          })}
        </div>
      }
    </StyledDragList>
  );
};
